import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import { CheckCircle, ArrowRight, Clock, Calendar, User } from 'lucide-react';
import ShareOwenContextModal from '../components/coaching/ShareOwenContextModal';

const API = getApiUrl();

// localStorage helpers — le choix "refusé" est persistant pour ne pas re-harceler
const SHARE_KEY = (id) => `owen_share_decision_${id}`;
const getDecision = (id) => { try { return localStorage.getItem(SHARE_KEY(id)); } catch { return null; } };
const setDecision = (id, v) => { try { localStorage.setItem(SHARE_KEY(id), v); } catch { /* noop */ } };

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [showShareModal, setShowShareModal] = useState(false);

  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!bookingId || !token) return;

    let attempts = 0;
    const maxAttempts = 8;

    const pollStatus = async () => {
      try {
        const res = await fetch(`${API}/marketplace/bookings/${bookingId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Erreur');
        const data = await res.json();
        setBooking(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          setLoading(false);
          // Propose le partage de contexte Owen (opt-in, 1 seule fois si pas décidé)
          if (bookingId && !getDecision(bookingId)) {
            setTimeout(() => setShowShareModal(true), 800);
          }
          return;
        }

        if (data.status === 'cancelled' || data.payment_status === 'expired') {
          setStatus('error');
          setLoading(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2500);
        } else {
          setStatus('success');
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setStatus('error');
        setLoading(false);
      }
    };

    pollStatus();
  }, [bookingId, token]);

  return (
    <div className="theme-light min-h-screen flex items-center justify-center px-4">
      {showShareModal && bookingId && (
        <ShareOwenContextModal
          bookingId={bookingId}
          coachName={booking?.coach_name}
          sessionDate={booking?.date}
          token={token}
          onClose={() => {
            setDecision(bookingId, 'dismissed');
            setShowShareModal(false);
          }}
          onSuccess={() => {
            setDecision(bookingId, 'shared');
            setShowShareModal(false);
          }}
        />
      )}
      <div className="max-w-md w-full text-center">
        {loading ? (
          <div>
            <div className="w-16 h-16 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin mx-auto mb-6" />
            <h2 className="text-xl text-[#111827] mb-2">Vérification du paiement...</h2>
            <p className="text-[#6B7280] text-sm">Merci de patienter quelques instants.</p>
          </div>
        ) : status === 'success' ? (
          <div>
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-light text-[#111827] mb-2">Réservation confirmée</h2>
            <p className="text-[#6B7280] text-sm mb-8">
              Votre session de coaching a été réservée avec succès.
            </p>

            {booking && (
              <div className="bg-white border border-[#E8E5DE] rounded-xl p-5 text-left mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#6B7280] text-sm">Coach: <span className="text-[#111827]">{booking.coach_name}</span></span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#6B7280] text-sm">Date: <span className="text-[#111827]">{booking.date}</span></span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-[#6B7280] text-sm">
                    Heure: <span className="text-[#111827]">{booking.start_time} - {booking.end_time}</span>
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button data-testid="go-to-my-bookings"
                onClick={() => navigate('/my-bookings')}
                className="w-full py-3 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all flex items-center justify-center gap-2">
                Mes réservations <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate('/marketplace')}
                className="w-full py-3 bg-transparent border border-[#DDD9D0] text-[#111827] rounded-full text-sm hover:bg-white/5 transition-all">
                Retour &agrave; l'Espace Coachs
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-400 text-2xl">!</span>
            </div>
            <h2 className="text-xl text-[#111827] mb-2">Problème de paiement</h2>
            <p className="text-[#6B7280] text-sm mb-6">Le paiement n'a pas pu être vérifié. Veuillez réessayer.</p>
            <button onClick={() => navigate('/marketplace')}
              className="px-6 py-3 bg-[#D4AF37] text-[#0A0A0B] rounded-full font-medium text-sm hover:bg-[#F3D060] transition-all">
              Retour &agrave; l'Espace Coachs
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
